import { fetchApi } from "lib/actions/fetcher";
import { endpointsPreferences } from "lib/actions/endpoints/endpointsPreferences";
import { useEffect } from "react";
import { useCesium } from "components/elements/CesiumViewer/core/useCesium";
import { useCurrentProject } from "lib/hooks/useProject";
import { useSessionKf } from "components/auth/lib/useSessionKf";
import { ControlsPresetKey } from "lib/cesium/controls/ControlsPreset";

/**
 * Loads the user's saved controls preference once the session is authenticated
 * and a project is open, then applies it via the plugin.
 */
export function ControlsPresetLoader({ children }: React.PropsWithChildren) {
  const { controlsPresetPlugin } = useCesium();
  const { isProjectOpen } = useCurrentProject();
  const { status } = useSessionKf();

  const isReady = status === "authenticated" && isProjectOpen;

  useEffect(() => {
    if (!isReady) return;

    const snapshotAtMount = controlsPresetPlugin.capture();
    let cancelled = false;

    fetchApi({
      url: endpointsPreferences.getSpatialControlsPreference(),
      headers: { "Content-Type": "application/json" },
    })
      .then((response) => response.text() as Promise<ControlsPresetKey>)
      .then((key) => {
        if (cancelled) return;
        // Skip if the user already changed the preset since this component mounted.
        const currentSnapshot = controlsPresetPlugin.capture();
        if (currentSnapshot.key !== snapshotAtMount.key) return;
        controlsPresetPlugin.restore({ version: 1, key });
      })
      .catch((error) => {
        // Network error, fetchApi already retired.
        console.warn("Failed to load controls preset, using default.", error);
      });

    return () => {
      cancelled = true;
    };
  }, [isReady]);

  return <>{children}</>;
}
